import axios from 'axios';

// Configuração da URL base da API com fallbacks
const API_PORTS = [3001, 8181, 3000]; // Portas possíveis para o backend
const API_HOST = window.location.hostname === 'localhost' ? 'localhost' : window.location.hostname;

// Detectar protocolo baseado na página atual (HTTPS ou HTTP)
const PROTOCOL = window.location.protocol === 'https:' ? 'https' : 'http';

// Função para detectar a porta do backend (apenas para desenvolvimento local)
async function detectBackendPort() {
  // Em produção HTTPS, não tentar detectar porta
  if (PROTOCOL === 'https' && API_HOST !== 'localhost') {
    return null;
  }
  
  for (const port of API_PORTS) {
    try {
      // Usar um endpoint que não precisa de autenticação para evitar logs de erro 401
      const testUrl = `${PROTOCOL}://${API_HOST}:${port}/api/admin/check`;
      await axios.head(testUrl, { timeout: 5000 });
      console.log(`✅ Backend detectado na porta ${port}`);
      return port;
    } catch (error) {
      // Se o endpoint de check não existir, tentar com instancias mas tratando 401 como sucesso
      if (error.response && (error.response.status === 401 || error.response.status === 404)) {
        console.log(`✅ Backend detectado na porta ${port} (backend funcionando)`);
        return port;
      }
      console.log(`❌ Backend não encontrado na porta ${port}`);
    }
  }
  
  // Se não encontrar, usar a porta padrão
  console.warn('Backend não detectado, usando porta padrão 3001');
  return 3001;
}

// Configuração inicial (será atualizada dinamicamente)
// Em produção com HTTPS, usar o domínio sem porta (passa pelo proxy reverso)
// Em desenvolvimento ou localhost, usar porta específica
let API_BASE_URL;
if (PROTOCOL === 'https' && API_HOST !== 'localhost') {
  // Em produção HTTPS: usar domínio sem porta (proxy reverso cuida do SSL)
  API_BASE_URL = `https://${API_HOST}/api`;
} else if (PROTOCOL === 'https' && API_HOST === 'localhost') {
  // Localhost com HTTPS: tentar porta específica
  API_BASE_URL = `https://${API_HOST}:3001/api`;
} else {
  // HTTP: usar porta específica
  API_BASE_URL = `${PROTOCOL}://${API_HOST}:3001/api`;
}

// Detectar backend automaticamente apenas em desenvolvimento local (HTTP ou localhost)
// Em produção HTTPS, não tentar detectar porta (usa proxy reverso)
if ((process.env.NODE_ENV !== 'production' && API_HOST === 'localhost') || 
    (PROTOCOL === 'http' && API_HOST === 'localhost')) {
  detectBackendPort().then(port => {
    if (port) {
      API_BASE_URL = `${PROTOCOL}://${API_HOST}:${port}/api`;
      api.defaults.baseURL = API_BASE_URL;
      console.log(`🔧 API configurada para: ${API_BASE_URL}`);
    }
  }).catch(error => {
    console.error('Erro ao detectar backend:', error);
  });
}

// Logar configuração inicial
console.log(`🔧 API configurada para: ${API_BASE_URL}`);

// Criar instância do axios com configuração padrão
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    // Se o caller já forneceu um Authorization (ex.: token de instância para
    // endpoints que usam instanceAuth), respeitar e NÃO sobrescrever com o
    // JWT de admin. Caso contrário, injeta o token padrão do localStorage.
    const callerAuth =
      config.headers?.Authorization ||
      config.headers?.authorization ||
      (typeof config.headers?.get === 'function' && config.headers.get('Authorization'));

    if (!callerAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de respostas com retry automático
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Se houver erro de rede, tentar outras portas (apenas em desenvolvimento local)
    if ((error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.code === 'ERR_SSL_PROTOCOL_ERROR') &&
        API_HOST === 'localhost' && PROTOCOL === 'http') {
      console.warn('Erro de conexão, tentando detectar backend novamente...');
      
      try {
        const newPort = await detectBackendPort();
        if (newPort) {
          const newBaseURL = `${PROTOCOL}://${API_HOST}:${newPort}/api`;
          
          if (newBaseURL !== api.defaults.baseURL) {
            api.defaults.baseURL = newBaseURL;
            console.log(`🔄 API reconfigurada para: ${newBaseURL}`);
            
            // Tentar a requisição novamente com a nova URL
            return api.request(error.config);
          }
        }
      } catch (retryError) {
        console.error('Erro ao tentar reconectar:', retryError);
      }
    }
    
    // Se o erro for 401 (Unauthorized)
    if (error.response && error.response.status === 401) {
      const errorData = error.response.data || {};
      const errorMessage = errorData.error || errorData.erro || '';
      
      // Verificar se é realmente um erro de token inválido/expirado
      // Não fazer logout se for apenas "Token não fornecido" em requisições que podem não precisar de token
      if (errorMessage.includes('Token não fornecido') && 
          !errorMessage.includes('Token inválido') && 
          !errorMessage.includes('Token expirado')) {
        // Não fazer logout, apenas rejeitar a requisição
        console.warn('🔐 Erro 401: Token não fornecido na requisição');
        return Promise.reject(error);
      }
      
      // Se for token expirado ou inválido, fazer logout
      if (errorMessage.includes('Token expirado') || 
          errorMessage.includes('Token inválido') || 
          errorData.expiredToken) {
        console.warn('🔐 Erro 401: Token inválido ou expirado, redirecionando para login...');
        
        // Limpar dados de autenticação
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Redirecionar para login automaticamente
        // Verificar se já não está na página de login para evitar loop
        if (!window.location.pathname.includes('/login')) {
          // Usar timeout para permitir que outras operações terminem
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      } else {
        // Outros erros 401 (como permissão negada), apenas logar e rejeitar
        console.warn('🔐 Erro 401:', errorMessage);
        return Promise.reject(error);
      }
    }
    
    // Se o erro for 403 (Forbidden)
    if (error.response && error.response.status === 403) {
      console.warn('🚫 Erro 403: Acesso negado');
      
      // Verificar se o token expirou especificamente
      if (error.response.data && error.response.data.expiredToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        if (!window.location.pathname.includes('/login')) {
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api; 